import React, { useState, useEffect } from 'react';
import { useNotification, NOTIFICATION_TYPES } from './NotificationContext';
import { 
  CheckIcon, 
  ExclamationTriangleIcon, 
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import '../styles/notifications.css';
import useTranslation from '../services/translationService';

const NotificationItem = ({ notification, onRemove }) => {
  const { t } = useTranslation();
  const [isExiting, setIsExiting] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Min swipe distance in pixels
  const minSwipeDistance = 50;

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    // If swiped left or right, dismiss
    if (isLeftSwipe || isRightSwipe) {
      handleDismiss();
    }
    
    // Reset touch coordinates
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(notification.id);
    }, 300); // Match the animation duration
  };

  const styles = {
    [NOTIFICATION_TYPES.SUCCESS]: {
      bg: 'bg-green-50 dark:bg-green-900/80',
      border: 'border-green-200 dark:border-green-600',
      text: 'text-green-800 dark:text-green-100',
      icon: <CheckIcon className="w-5 h-5" />,
      iconBg: 'bg-green-100 dark:bg-green-700'
    },
    [NOTIFICATION_TYPES.WARNING]: {
      bg: 'bg-amber-50 dark:bg-amber-900/80',
      border: 'border-amber-200 dark:border-amber-600',
      text: 'text-amber-800 dark:text-amber-100',
      icon: <ExclamationTriangleIcon className="w-5 h-5" />,
      iconBg: 'bg-amber-100 dark:bg-amber-700'
    },
    [NOTIFICATION_TYPES.ERROR]: {
      bg: 'bg-red-50 dark:bg-red-900/80',
      border: 'border-red-200 dark:border-red-600',
      text: 'text-red-800 dark:text-red-100',
      icon: <XMarkIcon className="w-5 h-5" />,
      iconBg: 'bg-red-100 dark:bg-red-700'
    },
    [NOTIFICATION_TYPES.INFO]: {
      bg: 'bg-blue-50 dark:bg-blue-900/80',
      border: 'border-blue-200 dark:border-blue-600',
      text: 'text-blue-800 dark:text-blue-100',
      icon: <InformationCircleIcon className="w-5 h-5" />,
      iconBg: 'bg-blue-100 dark:bg-blue-700'
    }
  };

  const type = notification.type in styles ? notification.type : NOTIFICATION_TYPES.INFO;
  const style = styles[type];

  return (
    <div
      className={`rounded-lg shadow-lg border overflow-hidden ${style.bg} ${style.border}
        transition-all duration-300 ease-in-out transform
        ${isExiting ? 'animate-slide-out-right' : 'animate-fade-in-slide-up'}`}
      role="alert"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="flex items-center p-3">
        <div className={`flex-shrink-0 ${style.iconBg} ${style.text} p-2 rounded-full mr-3`}>
          {style.icon}
        </div>
        <div className={`flex-grow text-sm font-medium ${style.text} mr-2`}>
          {notification.message}
        </div>
        <button
          onClick={handleDismiss}
          className={`flex-shrink-0 rounded-md ${style.text} hover:bg-opacity-20 hover:bg-black p-1.5 transition-colors duration-200`}
          aria-label={t('common.close', 'Close')}
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
      
      {/* Auto-dismiss progress bar */}
      <div className="relative h-1 w-full bg-gray-200 dark:bg-gray-700">
        <div 
          className={`absolute left-0 top-0 h-full ${type === NOTIFICATION_TYPES.ERROR ? 'bg-red-500' : 'bg-blue-500'} animation-shrink-width`}
        />
      </div>
    </div>
  );
};

const NotificationToast = () => {
  const { notifications, removeNotification } = useNotification();

  if (notifications.length === 0) {
    return null;
  }

  return (
    <>
      {/* Desktop positioning */}
      <div className="hidden sm:block fixed top-4 right-4 z-[9999] w-80 md:w-96 space-y-3 pointer-events-none">
        <div className="pointer-events-auto space-y-3">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRemove={removeNotification}
            />
          ))}
        </div>
      </div>

      {/* Mobile positioning */}
      <div className="sm:hidden fixed top-2 left-2 right-2 z-[9999] space-y-2 pointer-events-none">
        <div className="pointer-events-auto space-y-2">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRemove={removeNotification}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default NotificationToast; 